import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useCallback, useContext } from "react";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import { startCase } from "lodash";
import {
    AGENDA_VOTING_PROCESS,
    AGENDA_VOTING_PROCESS_STEP
} from "src/stores/assemblies/AgendaVotingProgressModalStore";
import styles from "./AgendaVotingProgressModal.module.css";
import { AgendaVotingInformation } from "./AgendaVotingInformation";
import { AgendaVotingOptions } from "./AgendaVotingOptions";
import { runInAction } from "mobx";

const ProgressStepStartVotingButtons = observer(function ProgressStepStartVotingButtons() {
    const { t } = useTranslation();
    const { votingStore, agendaVotingProgressModalStore, assemblyStore } = useContext(RootStoreContext);

    const onCloseModal = useCallback(async () => {
        if (votingStore.isNewVoting && votingStore.votingBasis.votingId.length > 0) {
            await votingStore.deleteVotingById(votingStore.votingBasis.votingId);
        }
        votingStore.resetAllVotingData();
        votingStore.setIsVotingModalDisplayed(false);
        agendaVotingProgressModalStore.resetCurrentProcessType();
    }, [votingStore, agendaVotingProgressModalStore]);

    const onClickStep = useCallback(
        (processType: AGENDA_VOTING_PROCESS) => () => {
            agendaVotingProgressModalStore.setCurrentProcessType(processType);
        },
        [agendaVotingProgressModalStore]
    );

    return (
        <UI.Row className={`mt-5 ${styles.ButtonRow}`}>
            <UI.Col xs={12} md={3}>
                <UI.Button
                    className={"mt-2"}
                    label={startCase(t("labels.cancel").toString())}
                    variant={"outline-primary"}
                    onClick={onCloseModal}
                ></UI.Button>
            </UI.Col>
            <UI.Col xs={12} md={3}>
                <UI.Button
                    className={"mt-2"}
                    label={t("labels.continue").charAt(0).toUpperCase() + t("labels.continue").slice(1)}
                    onClick={onClickStep(AGENDA_VOTING_PROCESS.AGENDA_VOTING_OPTIONS)}
                    disabled={!assemblyStore.isAssemblyReadOnly && !votingStore.isVotingBasisValid}
                ></UI.Button>
            </UI.Col>
        </UI.Row>
    );
});

const ProgressStepVotingOptionsButtons = observer(function ProgressStepVotingOptionsButtons() {
    const { t } = useTranslation();
    const { votingStore, agendaVotingProgressModalStore, agendaitemStore, assemblyStore } =
        useContext(RootStoreContext);

    const onCloseModal = useCallback(async () => {
        if (votingStore.isNewVoting && votingStore.votingBasis.votingId.length > 0) {
            await votingStore.deleteVotingById(votingStore.votingBasis.votingId);
        }
        votingStore.resetAllVotingData();
        votingStore.setIsVotingModalDisplayed(false);
        agendaVotingProgressModalStore.resetCurrentProcessType();
    }, [votingStore, agendaVotingProgressModalStore]);

    const onClickStep = useCallback(
        (processType: AGENDA_VOTING_PROCESS) => () => {
            agendaVotingProgressModalStore.setCurrentProcessType(processType);
        },
        [agendaVotingProgressModalStore]
    );

    const onSaveAllVotingInformation = useCallback(async () => {
        await votingStore.saveVotingInformationBasis();
        if (votingStore.isNewVoting || votingStore.isVotingOptionsModified) {
            await votingStore.saveVotingOptions();
        }

        const agendaItemId = agendaitemStore.currentAgendaItem?.id;
        if (agendaItemId) {
            runInAction(() => {
                votingStore.lastLoadedAgendaitemId = null;
                votingStore.setLastLoadedVotingContextKey("");
            });
            await votingStore.loadAllVotingsByAgendaItemId(agendaItemId);
        }

        votingStore.setIsVotingModalDisplayed(false);
        agendaVotingProgressModalStore.resetCurrentProcessType();
        await agendaitemStore.loadAgendaItems();
    }, [votingStore, agendaVotingProgressModalStore, agendaitemStore]);

    return (
        <UI.Row className={"mt-5"}>
            <UI.Col xs={12} md={3}>
                <UI.Button
                    className={"mt-2"}
                    label={startCase(t("labels.cancel").toString())}
                    variant={"outline-primary"}
                    onClick={onCloseModal}
                />
            </UI.Col>
            <UI.Col className={styles.RightButtonGroup}>
                <UI.Col xs={12} md={4}>
                    <UI.Button
                        className={"mt-2"}
                        label={t("labels.goback").toString()}
                        variant={"outline-primary"}
                        onClick={onClickStep(AGENDA_VOTING_PROCESS.START_AGENDA_VOTING)}
                    />
                </UI.Col>
                <UI.Col xs={12} md={4}>
                    <UI.Button
                        className="mt-2"
                        label={t("labels.save")}
                        onClick={onSaveAllVotingInformation}
                        disabled={!votingStore.isRealestateSelected || assemblyStore.isAssemblyReadOnly}
                    />
                </UI.Col>
            </UI.Col>
        </UI.Row>
    );
});

export const AgendaVotingProgressModal = observer(function AgendaVotingProgressModal() {
    const { t } = useTranslation();
    const { uiStore, votingStore, agendaVotingProgressModalStore } = useContext(RootStoreContext);
    const { currentStepType, currentStep, VOTING_MODAL_MAX_NUMBER_OF_STEPS } = agendaVotingProgressModalStore;

    const onCloseModal = useCallback(async () => {
        if (votingStore.isNewVoting && votingStore.votingBasis.votingId.length > 0) {
            await votingStore.deleteVotingById(votingStore.votingBasis.votingId);
        }
        votingStore.resetAllVotingData();
        votingStore.setIsVotingModalDisplayed(false);
        agendaVotingProgressModalStore.resetCurrentProcessType();
    }, [votingStore, agendaVotingProgressModalStore]);

    const renderStep = () => {
        switch (currentStepType) {
            case AGENDA_VOTING_PROCESS_STEP.START_AGENDA_VOTING:
            default:
                return (
                    <>
                        <AgendaVotingInformation />
                        <ProgressStepStartVotingButtons />
                    </>
                );
            case AGENDA_VOTING_PROCESS_STEP.AGENDA_VOTING_OPTIONS:
                return (
                    <>
                        <AgendaVotingOptions />
                        <ProgressStepVotingOptionsButtons />
                    </>
                );
        }
    };

    return (
        <UI.ProgressModal
            currentStep={currentStep?.stepNumber ?? 1}
            totalSteps={VOTING_MODAL_MAX_NUMBER_OF_STEPS}
            title={t("screens.assemblies.vote.voting")}
            show={votingStore.isVotingModalDisplayed}
            onDismissModal={onCloseModal}
            centered={uiStore.isMobile ? true : false}
            size={uiStore.isMobile ? undefined : "lg"}
        >
            {renderStep()}
        </UI.ProgressModal>
    );
});
